import React, { useState, useEffect, useMemo, useRef } from "react";
import { itemsAPI, gestoresAPI, uploadAPI, getNextCodSis, getUserName } from "../utils/api";
import SignatureModal from './SignatureModal';
import SignatureViewer from './SignatureViewer';

// Configuração da API
const API_BASE_URL = 'https://vega-solutions.site/api';

// Injeção do Tailwind CSS via CDN para compatibilidade no editor
const TailwindCSS = () => (
  <script src="https://cdn.tailwindcss.com"></script>
);

// Estilos responsivos para o sistema
const ResponsiveStyles = () => (
  <style>{`
    /* Estilos responsivos para diferentes tamanhos de tela */
    
    /* Header responsivo */
    .page-header {
      padding: 1rem;
      background: white;
      color: #1f2937;
      border-radius: 12px;
      margin-bottom: 1.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
    }
    
    .page-title {
      font-size: clamp(1.5rem, 4vw, 2.5rem);
      font-weight: 700;
      margin-bottom: 1rem;
      text-align: center;
      color: #1f2937;
    }
    
    /* Botões responsivos */
    .header-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      justify-content: center;
      align-items: center;
    }
    
    .action-group {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      justify-content: center;
    }
    
    .pending-group {
      background: #fef3c7;
      padding: 0.5rem;
      border-radius: 8px;
      border: 1px solid #f59e0b;
    }
    
    .btn {
      padding: 0.5rem 1rem;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.3s ease;
      white-space: nowrap;
      min-width: fit-content;
    }
    
    .btn-primary {
      background: #3b82f6;
      color: white;
    }
    
    .btn-secondary {
      background: #6b7280;
      color: white;
    }
    
    .btn-warning {
      background: #f59e0b;
      color: white;
    }
    
    .btn-success {
      background: #10b981;
      color: white;
    }
    
    .btn-info {
      background: #06b6d4;
      color: white;
    }
    
    .btn-outline {
      background: transparent;
      border: 2px solid #e5e7eb;
      color: #374151;
    }
    
    .btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    .btn-sm {
      padding: 0.375rem 0.75rem;
      font-size: 0.75rem;
    }
    
    .btn-lg {
      padding: 0.75rem 1.5rem;
      font-size: 1rem;
    }
    
    /* Grid responsivo para gestores */
    .gestores-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }
    
    .gestor-card {
      background: white;
      border-radius: 12px;
      padding: 1.5rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
    }
    
    .card-header h3 {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: #1f2937;
    }
    
    .card-header p {
      color: #6b7280;
      font-size: 0.875rem;
      margin-bottom: 1rem;
    }
    
    /* Filtros responsivos */
    .filters-section {
      background: white;
      border-radius: 12px;
      padding: 1.5rem;
      margin-bottom: 2rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    
    .filters-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      align-items: end;
    }
    
    .filter-input, .filter-select {
      width: 100%;
      padding: 0.75rem;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      font-size: 0.875rem;
      transition: border-color 0.3s ease;
    }
    
    .filter-input:focus, .filter-select:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    .date-filters-row {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      margin: 1rem 0;
    }
    
    .date-filter {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    
    .date-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #374151;
    }
    
    .date-input {
      width: 100%;
      padding: 0.75rem;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      font-size: 0.875rem;
      transition: border-color 0.3s ease;
    }
    
    .date-input:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    .clear-filters {
      grid-column: 1 / -1;
      display: flex;
      justify-content: center;
      margin-top: 1rem;
    }
    
    /* Tabela responsiva */
    .table-section {
      background: white;
      border-radius: 12px;
      padding: 1.5rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      overflow-x: auto;
    }
    
    .data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.875rem;
      min-width: 1200px;
    }
    
    .data-table th {
      background: #f8fafc;
      padding: 1rem 0.75rem;
      text-align: left;
      font-weight: 600;
      color: #374151;
      border-bottom: 2px solid #e5e7eb;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    .data-table td {
      padding: 0.75rem;
      border-bottom: 1px solid #e5e7eb;
      vertical-align: top;
    }
    
    .data-table tr:hover {
      background: #f8fafc;
    }
    
    .table-select {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 0.75rem;
      background: white;
    }
    
    /* Modais responsivos */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
    }
    
    .modal {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
      max-width: 90vw;
      max-height: 90vh;
      width: 100%;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    
    .modal-wide {
      max-width: 95vw;
      width: 100%;
    }
    
    .modal-header {
      padding: 1.5rem;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;
    }
    
    .modal-header h3 {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }
    
    .modal-body {
      padding: 1.5rem;
      overflow-y: auto;
      flex: 1;
    }
    
    .modal-footer {
      padding: 1.5rem;
      border-top: 1px solid #e5e7eb;
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
      flex-wrap: wrap;
      flex-shrink: 0;
    }
    
    /* Modal de cadastro manual */
    .modal-title-section {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    
    .modal-icon {
      font-size: 2rem;
    }
    
    .modal-subtitle {
      color: #6b7280;
      font-size: 0.875rem;
      margin: 0;
    }
    
    .manual-import-content {
      max-width: 800px;
      margin: 0 auto;
    }
    
    .import-steps {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      margin-bottom: 2rem;
    }
    
    .step-item {
      text-align: center;
      padding: 1rem;
      background: #f8fafc;
      border-radius: 8px;
    }
    
    .step-number {
      width: 40px;
      height: 40px;
      background: #3b82f6;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      margin: 0 auto 1rem;
    }
    
    .step-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      color: #6b7280;
    }
    
    .import-actions {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }
    
    .action-card {
      background: white;
      border: 2px solid #e5e7eb;
      border-radius: 12px;
      padding: 1.5rem;
      text-align: center;
      transition: all 0.3s ease;
    }
    
    .action-card:hover {
      border-color: #3b82f6;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
    }
    
    .action-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
    }
    
    .action-content h4 {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: #1f2937;
    }
    
    .action-content p {
      color: #6b7280;
      margin-bottom: 1.5rem;
    }
    
    .action-btn {
      width: 100%;
      padding: 1rem;
      font-size: 1rem;
    }
    
    .btn-icon {
      margin-right: 0.5rem;
    }
    
    .upload-btn {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .hidden {
      display: none;
    }
    
    .import-info {
      background: #f0f9ff;
      border: 1px solid #bae6fd;
      border-radius: 8px;
      padding: 1rem;
    }
    
    .info-card {
      display: flex;
      gap: 1rem;
      align-items: flex-start;
    }
    
    .info-icon {
      font-size: 1.5rem;
      flex-shrink: 0;
    }
    
    .info-content h5 {
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: #1f2937;
    }
    
    .info-content ul {
      margin: 0;
      padding-left: 1.5rem;
      color: #374151;
    }
    
    .info-content li {
      margin-bottom: 0.25rem;
    }
    
    /* Layout horizontal para modais grandes */
    .modal-body-horizontal {
      display: flex;
      gap: 1.5rem;
      min-height: 400px;
    }
    
    .modal-left-panel, .modal-right-panel {
      flex: 1;
      min-width: 0;
    }
    
    /* Configurações responsivas */
    .config-grid-horizontal {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
    }
    
    .config-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    
    .config-group label {
      font-weight: 600;
      color: #374151;
      font-size: 0.875rem;
    }
    
    /* Tabelas em modais */
    .items-table-horizontal {
      max-height: 300px;
      overflow-y: auto;
    }
    
    .items-table-horizontal table {
      width: 100%;
      font-size: 0.75rem;
    }
    
    .items-table-horizontal th,
    .items-table-horizontal td {
      padding: 0.5rem;
      border-bottom: 1px solid #e5e7eb;
      white-space: nowrap;
    }
    
    /* Paginação responsiva */
    .pagination-section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      align-items: center;
      margin-top: 1.5rem;
      padding: 1rem;
      background: #f8fafc;
      border-radius: 8px;
    }
    
    .pagination-controls {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      justify-content: center;
    }
    
    .pagination-numbers {
      display: flex;
      gap: 0.25rem;
    }
    
    .pagination-input {
      width: 60px;
      padding: 0.5rem;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      text-align: center;
    }
    
    /* Resumo responsivo */
    .summary-section {
      margin-bottom: 2rem;
    }
    
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
    }
    
    .summary-card {
      background: white;
      border-radius: 12px;
      padding: 1.5rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    
    .summary-icon {
      font-size: 2rem;
    }
    
    .summary-content {
      flex: 1;
    }
    
    .summary-label {
      font-size: 0.875rem;
      color: #6b7280;
      margin-bottom: 0.25rem;
    }
    
    .summary-value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1f2937;
    }
    
    /* Responsividade para telas pequenas */
    @media (max-width: 768px) {
      .header-actions {
        flex-direction: column;
        align-items: stretch;
      }
      
      .btn {
        width: 100%;
        margin-bottom: 0.5rem;
      }
      
      .gestores-grid {
        grid-template-columns: 1fr;
      }
      
      .filters-grid {
        grid-template-columns: 1fr;
      }
      
      .modal-body-horizontal {
        flex-direction: column;
      }
      
      .config-grid-horizontal {
        grid-template-columns: 1fr;
      }
      
      .pagination-controls {
        flex-direction: column;
        align-items: center;
      }
      
      .data-table {
        font-size: 0.75rem;
      }
      
      .data-table th,
      .data-table td {
        padding: 0.5rem 0.25rem;
      }
    }
    
    @media (max-width: 480px) {
      .page-header {
        padding: 0.75rem;
      }
      
      .gestor-card,
      .filters-section,
      .table-section {
        padding: 1rem;
      }
      
      .modal {
        margin: 0.5rem;
        max-width: calc(100vw - 1rem);
      }
      
      .modal-header,
      .modal-body,
      .modal-footer {
        padding: 1rem;
      }
    }

    /* Estilos para Modal de Retry */
    .retry-items-container {
      max-height: 60vh;
      overflow-y: auto;
    }

    .retry-summary {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem;
      background: #f8fafc;
      border-radius: 8px;
      margin-bottom: 1rem;
      border: 1px solid #e2e8f0;
    }

    .retry-count {
      font-weight: 600;
      color: #dc2626;
    }

    .retry-corrected {
      font-weight: 600;
      color: #16a34a;
    }

    .retry-items-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .retry-item {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 1rem;
      background: #ffffff;
      transition: all 0.3s ease;
    }

    .retry-item.corrected {
      border-color: #16a34a;
      background: #f0fdf4;
    }

    .retry-item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid #e2e8f0;
    }

    .item-cod-sis {
      font-weight: 600;
      color: #1e293b;
      font-size: 1.1rem;
    }

    .item-error {
      color: #dc2626;
      font-size: 0.9rem;
      font-style: italic;
    }

    .corrected-badge {
      background: #16a34a;
      color: white;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .retry-item-fields {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
    }

    .field-group {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .field-group label {
      font-weight: 600;
      color: #374151;
      font-size: 0.9rem;
    }

    .field-group input,
    .field-group select {
      padding: 0.5rem;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      font-size: 0.9rem;
      transition: border-color 0.3s ease;
    }

    .field-group input:focus,
    .field-group select:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .retry-item.corrected .field-group input,
    .retry-item.corrected .field-group select {
      border-color: #16a34a;
    }
  `}</style>
);

// Funções auxiliares para a prévia (não parte do código original)
// Mock para o parsing do XML, substitua pela sua implementação real.
function parseNFeXMLBrowser(xmlText) {
  console.log("Mock de parseNFeXMLBrowser executado.");
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, "text/xml");
  
  const nfNumber = xmlDoc.querySelector("nNF")?.textContent || "000000";
  const issueDate = xmlDoc.querySelector("dhEmi")?.textContent?.slice(0, 10) || "";
  const delivery = xmlDoc.querySelector("dest > xNome")?.textContent || "";
  // Buscar chNFe em diferentes locais
  let chNFe = "";
  
  // Método 1: protNFe > infProt > chNFe
  chNFe = xmlDoc.querySelector("protNFe infProt chNFe")?.textContent?.trim();
  
  // Método 2: infProt > chNFe
  if (!chNFe) {
    chNFe = xmlDoc.querySelector("infProt chNFe")?.textContent?.trim();
  }
  
  // Método 3: chNFe em qualquer lugar
  if (!chNFe) {
    chNFe = xmlDoc.querySelector("chNFe")?.textContent?.trim();
  }
  
  // Método 4: Extrair do ID do infNFe (formato: NFe + chNFe)
  if (!chNFe) {
    const infNFeId = xmlDoc.querySelector("infNFe")?.getAttribute("Id");
    if (infNFeId && infNFeId.startsWith("NFe")) {
      chNFe = infNFeId.substring(3); // Remove "NFe" do início
    }
  }
  
  // Método 5: Buscar por todos os elementos chNFe
  if (!chNFe) {
    const allChNFe = xmlDoc.querySelectorAll("chNFe");
    for (let element of allChNFe) {
      if (element.textContent && element.textContent.trim()) {
        chNFe = element.textContent.trim();
        break;
      }
    }
  }
  
  
  const infCpl = xmlDoc.querySelector("infCpl")?.textContent || '';
  const deliveryRegex = /DELIVERY:\s*(\d+)/;
  const pedidoRegex = /PED\.CLIENTE:\s*(.*?)(?:\s+Cubagem:|$)/;
  const deliveryCode = infCpl.match(deliveryRegex)?.[1] || '';
  const pedidoCode = infCpl.match(pedidoRegex)?.[1] || '';

  const items = Array.from(xmlDoc.querySelectorAll("det")).map(det => {
    return {
      cProd: det.querySelector("cProd")?.textContent || "N/A",
      xProd: det.querySelector("xProd")?.textContent || "N/A",
      qCom: det.querySelector("qCom")?.textContent || "1",
      vUnCom: det.querySelector("vUnCom")?.textContent || "0",
      vProd: det.querySelector("vProd")?.textContent || "0",
    };
  });
  return { nfNumber, issueDate, delivery, items, deliveryCode, pedidoCode, chNFe };
}

// Função de geração de etiquetas com layout 3x3
function openLabelsWindow(items) {
  const labelWindow = window.open('', '_blank', 'height=600,width=800');
  labelWindow.document.write(`
    <html>
    <head>
      <title>Etiquetas 3x3</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
      <style>
        body { 
          font-family: sans-serif; 
          margin: 0; 
          padding: 5mm; 
          background: white;
          text-align: left;
        }
        
        .page-container {
          display: flex;
          flex-direction: column;
          gap: 4mm;
          width: fit-content;
          margin: 0;
          align-items: flex-start;
        }
        
        .etiqueta-row {
          display: flex;
          flex-direction: row;
          gap: 4mm;
        }
        
        .etiqueta {
          width: 40mm;
          height: 30mm;
          border: 1px solid #000;
          padding: 2mm;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          font-size: 9pt;
          line-height: 1.1;
          overflow: hidden;
          background: white;
        }
        
        .etiqueta-vazia {
          border: none !important;
          background: transparent !important;
        }
        
        .etiqueta-linha {
          margin-bottom: 0.3mm;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          font-weight: bold;
        }
        
        .etiqueta-linha strong {
          font-weight: bold;
        }
        
        .barcode-container {
          width: 30mm;
          height: 8mm;
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0 auto;
          overflow: hidden;
        }
        
        .spacer {
          width: 2mm;
          height: 2mm;
        }
        
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          
          .page-container {
            page-break-after: always;
          }
          
          .etiqueta {
            border: none !important;
          }
          
          .spacer {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      ${generatePages(items)}
      <script>
        document.querySelectorAll('svg[id^="barcode-"]').forEach(svg => {
          const codSis = svg.id.replace('barcode-', '');
          JsBarcode("#" + svg.id, codSis, {
            format: "CODE128",
            width: 1,
            height: 40,
            displayValue: false,
            margin: 0,
          });
        });
      </script>
    </body>
    </html>
  `);
  
  labelWindow.document.close();
  labelWindow.focus();
}

// Função para gerar páginas com layout 3x3
function generatePages(items) {
  const itemsPerPage = 9; // 3x3
  const pages = [];
  
  // Se há 6 itens ou menos, criar apenas uma página
  if (items.length <= 6) {
    pages.push(generatePage(items));
  } else {
    // Para mais de 6 itens, usar paginação normal
    for (let i = 0; i < items.length; i += itemsPerPage) {
      const pageItems = items.slice(i, i + itemsPerPage);
      pages.push(generatePage(pageItems));
    }
  }
  
  return pages.join('');
}

// Função para gerar uma página 3x3
function generatePage(items) {
  const totalItems = items.length;
  const rowsNeeded = Math.ceil(totalItems / 3);
  
  // Criar etiquetas organizadas por linhas (3 etiquetas por linha)
  let html = '';
  
  for (let row = 0; row < rowsNeeded; row++) {
    html += '<div class="etiqueta-row">';
    
    for (let col = 0; col < 3; col++) {
      const index = row * 3 + col;
      const item = items[index];
      
      if (item) {
        html += `
          <div class="etiqueta">
            <div class="etiqueta-linha" style="display: flex; justify-content: space-between;">
              <span>${item.cod_sis || ''}</span>
              <span>${item.data_nf || ''}</span>
            </div>
            <div class="etiqueta-linha">${item.part_number || ''}</div>
            <div class="barcode-container">
              <svg id="barcode-${item.cod_sis}"></svg>
            </div>
            <div class="etiqueta-linha" style="display: flex; justify-content: space-between;">
              <span>${item.nf || ''}</span>
              <span>${item.valor_unit ? Number(item.valor_unit).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : ''}</span>
            </div>
            <div class="etiqueta-linha">${item.pedido_cliente || ''}</div>
            <div class="etiqueta-linha">${item.delivery || ''}</div>
          </div>
        `;
      } else {
        html += '<div class="etiqueta etiqueta-vazia"></div>';
      }
    }
    
    html += '</div>';
  }
  
  return `
    <div class="page-container">
      ${html}
    </div>
  `;
}

// Funções auxiliares para download de CSV
const downloadCSV = (data, filename) => {
  const sep = ';';
  const headers = ["COD. SIS", "ITEM SIS", "DATA NF", "NF", "ITEM NF", "LOTE", "LOCAL DO ESTOQUE", "PART NUMBER (COD. PEÇA)", "DESCRIÇÃO DA PEÇA", "Nº PEDIDO", "DELIVERY", "VALOR UNIT.", "VALOR TOTAL", "STATUS", "OS RELACIONADA", "LOCALIZAÇÃO FÍSICA", "ÚLTIMA ALTERAÇÃO", "LOG"];
  const esc = (s = "") => { const v = String(s); return (v.includes('"') || v.includes('\n') || v.includes(sep)) ? '"' + v.replace(/"/g, '""') + '"' : v };
  const rows = data.map(it => [
    it.cod_sis, (it.item_sis ?? 1), it.data_nf || "", it.nf || "", (it.item_nf ?? ""), it.lote || "", it.local_do_estoque || "", it.part_number || "", it.descricao || "", 
    it.pedido_cliente || "", it.delivery || "",
    String(Number(it.valor_unit || 0).toFixed(2)).replace('.', ','), String(Number(it.valor_total || 0).toFixed(2)).replace('.', ','),
    it.status || "", it.os_relacionada || "", it.localizacao_fisica || "", it.ultima_alteracao || "",
    (it.log || []).join("\n")
  ]);
  const csv = [headers.map(esc).join(sep), ...rows.map(r => r.map(esc).join(sep))].join('\n');
  const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const STATUSES = [
  "DISPONÍVEL EM ESTOQUE",
  "COM TÉCNICO",
  "UPC",
  "DEVOLVIDA COMO USADA",
  "DEVOLVIDA COMO DEFEITO",
  "DEVOLVIDA COMO NOVA",
  "ERRO DE BILLING",
  "ENTREGUE TRANSPORTADORA",
  "REQUISIÇÃO CANCELADA",
  "SERVICE DE RETORNO",
  "ENVIADO PARA UDI",
  "ENVIADO PARA CSP SSA",
  "ENVIADO PARA INSS SSA",
  "ENVIADO PARA INSS BARRA",
  "ENVIADO PARA CRC",
  "PRODUTO NOVO",
  "REPARO DE ESTOQUE",
  "BAIXAR PART SALES",
];

const nowISO = () => new Date().toISOString().slice(0, 19).replace("T", " ");
const brl = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const MANUAL_FIELDS = [
  "COD. SIS",
  "ITEM SIS",
  "DATA NF",
  "NF",
  "ITEM NF",
  "LOTE",
  "LOCAL DO ESTOQUE",
  "PART NUMBER (COD. PEÇA)",
  "DESCRIÇÃO DA PEÇA",
  "Nº PEDIDO",
  "DELIVERY",
  "VALOR UNIT.",
  "VALOR TOTAL",
  "STATUS",
  "OS RELACIONADA",
  "LOCALIZAÇÃO FÍSICA",
  "ÚLTIMA ALTERAÇÃO",
  "LOG"
];

export default function Gestores({ onNavigate }) {
  // Estados principais
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usedCodes, setUsedCodes] = useState(new Set());
  const [isCodesLoaded, setIsCodesLoaded] = useState(false);
  
  // Obter dados do usuário logado
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('currentUser');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Erro ao obter dados do usuário:', error);
      return null;
    }
  };
  
  const currentUser = getCurrentUser();
  const operadorId = currentUser?.operadorId;
  const userName = currentUser?.nome || 'Operador';
  
  
  // Função para verificar se o usuário é administrador
  const isAdmin = () => {
    return currentUser && currentUser.cargo === 'administrador';
  };
  
  // Função para carregar códigos existentes do banco
  const loadExistingCodes = async () => {
    if (isCodesLoaded) return;
    
    try {
      const response = await itemsAPI.getItems();
      if (response && response.length > 0) {
        const existingCodes = response
          .map(item => item.cod_sis)
          .filter(code => code && code.length === 7 && /^\d{7}$/.test(code));
        
        setUsedCodes(new Set(existingCodes));
        console.log(`${existingCodes.length} códigos existentes carregados`);
      }
      setIsCodesLoaded(true);
    } catch (error) {
      console.warn('Erro ao carregar códigos existentes:', error);
      setIsCodesLoaded(true);
    }
  };
  
  // Função para gerar código aleatório único de 7 dígitos
  const generateRandomCode = () => {
    let attempts = 0;
    const maxAttempts = 10000; // Aumentar tentativas para garantir unicidade
    
    while (attempts < maxAttempts) {
      // Gerar número aleatório de 7 dígitos (1000000 a 9999999)
      const randomCode = Math.floor(Math.random() * 9000000) + 1000000;
      const codeStr = String(randomCode);
      
      // Verificar se o código já foi usado
      if (!usedCodes.has(codeStr)) {
        // Adicionar ao conjunto de códigos usados
        setUsedCodes(prev => new Set([...prev, codeStr]));
        console.log(`✅ Código único gerado: ${codeStr} (tentativa ${attempts + 1})`);
        return codeStr;
      }
      
      attempts++;
    }
    
    // Se não conseguir gerar código único, usar combinação de timestamp + random
    const timestamp = Date.now().toString().slice(-4); // Últimos 4 dígitos
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0'); // 3 dígitos
    const fallbackCode = timestamp + random; // 7 dígitos total
    
    console.warn(`⚠️ Usando código de fallback: ${fallbackCode} (após ${maxAttempts} tentativas)`);
    setUsedCodes(prev => new Set([...prev, fallbackCode]));
    return fallbackCode;
  };
  
  // Função para gerar código único (sempre novo)
  const getOrCreateCodSis = async (partNumber, nf) => {
    // Carregar códigos existentes se necessário
    await loadExistingCodes();
    
    // Sempre gerar novo código aleatório único
    const newCode = generateRandomCode();
    console.log(`Gerando código único ${newCode} para peça ${partNumber} (NF: ${nf})`);
    return newCode;
  };
  
  // Função para gerar código aleatório (usar quando necessário)
  const nextCodSis = () => {
    return generateRandomCode();
  };

  // Estados de filtros
  const [fQ, setFQ] = useState("");
  const [fNF, setFNF] = useState("");
  const [fPN, setFPN] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [fDelivery, setFDelivery] = useState("");
  const [fOS, setFOS] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fLocal, setFLocal] = useState("");
  const [fLote, setFLote] = useState("");
  const [fDataNFDe, setFDataNFDe] = useState("");
  const [fDataNFAt, setFDataNFAt] = useState("");
  const [fUltAltDe, setFUltAltDe] = useState("");
  const [fUltAltAt, setFUltAltAt] = useState("");

  // Estados de modais
  const [showMove, setShowMove] = useState(false);
  const [showManualImport, setShowManualImport] = useState(false);
  const [showGenPN, setShowGenPN] = useState(false);
  const [askLabels, setAskLabels] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showSignatureViewer, setShowSignatureViewer] = useState(false);
  const [selectedItemSignature, setSelectedItemSignature] = useState(null);
  
  // Estados para seleção múltipla
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Estados para modal de retry de itens falhados
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [failedItems, setFailedItems] = useState([]);
  const [retryItems, setRetryItems] = useState([]);
  const [retryLoading, setRetryLoading] = useState(false);

  // Estados para movimentação
  const [moveBin, setMoveBin] = useState("");
  const [moveLocal, setMoveLocal] = useState("");
  const [moveLote, setMoveLote] = useState("");
  const [moveStatus, setMoveStatus] = useState("");
  const [moveNfDevolucao, setMoveNfDevolucao] = useState("");
  const [scanned, setScanned] = useState([]);
  const [osMap, setOsMap] = useState({});
  const [pendingCode, setPendingCode] = useState("");
  const [pendingOS, setPendingOS] = useState("");

  // Estados para etiquetas
  const [pnForLabels, setPnForLabels] = useState('');
  const [statusForLabels, setStatusForLabels] = useState('');

  // Estados de gestão
  const [lotes, setLotes] = useState([]);
  const [filtroLote, setFiltroLote] = useState("");
  const [selLote, setSelLote] = useState("");
  const [novoLote, setNovoLote] = useState("");

  const [estoques, setEstoques] = useState([]);
  const [filtroEstoque, setFiltroEstoque] = useState("");
  const [selEstoque, setSelEstoque] = useState("");
  const [novoEstoque, setNovoEstoque] = useState("");

  const [bins, setBins] = useState([]);
  const [filtroBin, setFiltroBin] = useState("");
  const [selBin, setSelBin] = useState("");
  const [novoBin, setNovoBin] = useState("");

  // Usar os mesmos status da configuração
  const statuses = STATUSES;
  
  // Usar apenas os locais (estoques) que pertencem ao operador logado
  const locais = useMemo(() => {
    const estoquesDoOperador = estoques.filter(e => {
      if (currentUser?.cargo === 'administrador') return true;
      return typeof e === 'object' ? e.operador_id === operadorId : false;
    });
    return estoquesDoOperador.map(e => typeof e === 'object' ? e.nome : e);
  }, [estoques, currentUser, operadorId]);
  
  // Obter lotes únicos dos itens existentes que pertencem ao operador logado
  const lotesDisponiveis = useMemo(() => {
    const uniqueLotes = [...new Set(items.map(item => item.lote).filter(Boolean))];
    // Filtrar apenas lotes que pertencem ao operador logado
    const lotesDoOperador = lotes.filter(l => {
      if (currentUser?.cargo === 'administrador') return true;
      return typeof l === 'object' ? l.operador_id === operadorId : false;
    }).map(l => typeof l === 'object' ? l.nome : l);
    
    // Combinar lotes dos itens com lotes do operador
    const todosLotes = [...new Set([...uniqueLotes, ...lotesDoOperador])];
    return todosLotes.length > 0 ? todosLotes : [];
  }, [items, lotes, currentUser, operadorId]);

  // Estados para upload
  const [pendingCreated, setPendingCreated] = useState([]);

  // Refs
  const codeInputRef = useRef(null);
  const osInputRef = useRef(null);
  const fileXMLRef = useRef(null);
  const fileManualRef = useRef(null);

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
    loadExistingCodes();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsResponse, lotesResponse, estoquesResponse, binsResponse] = await Promise.all([
        itemsAPI.getItems(),
        gestoresAPI.getLotes(),
        gestoresAPI.getEstoques(),
        gestoresAPI.getBins()
      ]);

        setItems((itemsResponse.data || []).map(it => ({
          ...it,
          // Normaliza logs do backend (`logs`) para o campo usado no frontend (`log`)
          log: Array.isArray(it.logs) ? it.logs : (it.log || [])
        })));

      // Carregar lotes
      if (lotesResponse.success && lotesResponse.data) {
        setLotes(lotesResponse.data.map(l => ({ id: l.id, nome: l.nome, operador_id: l.operador_id })));
      } else {
        setLotes([]);
      }

      // Carregar estoques
      if (estoquesResponse.success && estoquesResponse.data) {
        setEstoques(estoquesResponse.data.map(e => ({ id: e.id, nome: e.nome, operador_id: e.operador_id })));
      } else {
        setEstoques([]);
      }

      // Carregar bins
      if (binsResponse.success && binsResponse.data) {
        setBins(binsResponse.data.map(b => ({ id: b.id, nome: b.nome, operador_id: b.operador_id })));
      } else {
        setBins([]);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtros aplicados
  const like = (a, b) => String(a || "").toLowerCase().includes(String(b || "").toLowerCase());

  const filtered = useMemo(() => items.filter(it => {
    if (fQ && !(like(it.cod_sis, fQ) || like(it.nf, fQ) || like(it.part_number, fQ) || like(it.descricao, fQ) || like(it.delivery, fQ))) return false;
    if (fNF && !like(it.nf, fNF)) return false;
    if (fPN && !like(it.part_number, fPN)) return false;
    if (fDesc && !like(it.descricao, fDesc)) return false;
    if (fDelivery && !like(it.delivery, fDelivery)) return false;
    if (fOS && !like(it.os_relacionada, fOS)) return false;
    if (fStatus && it.status !== fStatus) return false;
    if (fLocal && it.localizacao_fisica !== fLocal) return false;
    if (fLote && it.lote !== fLote) return false;
    const dNF = String(it.data_nf || "");
    if (fDataNFDe && dNF < fDataNFDe) return false;
    if (fDataNFAt && dNF > fDataNFAt) return false;
    const dUA = String(it.ultima_alteracao || "").slice(0, 10);
    if (fUltAltDe && dUA < fUltAltDe) return false;
    if (fUltAltAt && dUA > fUltAltAt) return false;
    return true;
  }), [items, fQ, fNF, fPN, fDesc, fDelivery, fOS, fStatus, fLocal, fLote, fDataNFDe, fDataNFAt, fUltAltDe, fUltAltAt]);

  const totalUnidades = useMemo(() => filtered.reduce((s, it) => s + (Number(it.item_sis) || 0), 0), [filtered]);
  const valorTotal = useMemo(() => filtered.reduce((s, it) => s + (Number(it.valor_total) || 0), 0), [filtered]);

  // Lógica de paginação
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filtered.slice(startIndex, endIndex);

  // Reset para primeira página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [fQ, fNF, fPN, fDesc, fDelivery, fOS, fStatus, fLocal, fLote, fDataNFDe, fDataNFAt, fUltAltDe, fUltAltAt]);

  // Funções auxiliares
  const buildLog = (before, after, by, kind) => {
    const ts = nowISO();
    const seg = [];
    if ((before.localizacao_fisica || '') !== (after.localizacao_fisica || '')) seg.push(`${before.localizacao_fisica || '-'} → ${after.localizacao_fisica || '-'}`);
    if ((before.local_do_estoque || '') !== (after.local_do_estoque || '')) seg.push(`bin: ${before.local_do_estoque || '-'} → ${after.local_do_estoque || '-'}`);
    if ((before.lote || '') !== (after.lote || '')) seg.push(`lote: ${before.lote || '-'} → ${after.lote || '-'}`);
    if ((before.status || '') !== (after.status || '')) seg.push(`status: ${before.status || '-'} → ${after.status || '-'}`);
    if ((before.os_relacionada || '') !== (after.os_relacionada || '')) seg.push(`OS: ${before.os_relacionada || '-'} → ${after.os_relacionada || '-'}`);
    return `${ts} — ${by || 'Operador'} | ${kind} | ${seg.join(' | ')}`;
  };

  // Funções para etiquetas
  const resultsByPN = useMemo(() => items.filter(it => {
    const q = pnForLabels.trim().toUpperCase();
    if (!q) return false;
    const pn = String(it.part_number || '').trim().toUpperCase();
    if (pn !== q) return false;
    if (statusForLabels && it.status !== statusForLabels) return false;
    return true;
  }), [items, pnForLabels, statusForLabels]);

  // Funções para gestão de lotes
  const lotesFiltrados = useMemo(() => lotes.filter(l => {
    const nome = typeof l === 'object' ? l.nome : l;
    const loteOperadorId = typeof l === 'object' ? l.operador_id : null;
    
    // Se não for administrador, filtrar apenas lotes do mesmo operador ou lotes globais (operador_id null)
    if (currentUser?.cargo !== 'administrador' && operadorId) {
      if (loteOperadorId !== null && loteOperadorId !== operadorId) {
        return false;
      }
    }
    
    return String(nome || "").toLowerCase().includes(filtroLote.toLowerCase());
  }), [lotes, filtroLote, currentUser, operadorId]);

  // Funções para seleção múltipla
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(paginatedItems.map(item => item.id));
      setSelectedItems(allIds);
      setSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      window.alert('Selecione pelo menos um item para excluir.');
      return;
    }

    const confirmMessage = `Tem certeza que deseja excluir ${selectedItems.size} item(s) selecionado(s)?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Executar exclusões de forma SEQUENCIAL para não estourar conexões no MySQL (Hostinger)
      for (const id of Array.from(selectedItems)) {
        // Se desejar pequena simultaneidade, poderíamos usar filas em pequenos lotes
        await itemsAPI.deleteItem(id);
      }

      // Recarregar dados
      await loadData();

      // Limpar seleção
      setSelectedItems(new Set());
      setSelectAll(false);

      window.alert(`${selectedItems.size} item(s) excluído(s) com sucesso!`);
    } catch (error) {
      console.error('Erro ao excluir itens:', error);
      window.alert('Erro ao excluir itens. Tente novamente.');
    }
  };

  // Funções para modal de retry de itens falhados
  const openRetryModal = (failedItemsList) => {
    setFailedItems(failedItemsList);
    setRetryItems(failedItemsList.map(item => ({ ...item, corrected: false })));
    setShowRetryModal(true);
  };

  const closeRetryModal = () => {
    setShowRetryModal(false);
    setFailedItems([]);
    setRetryItems([]);
  };

  const updateRetryItem = (index, field, value) => {
    setRetryItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value, corrected: true } : item
    ));
  };

  const retryFailedItems = async () => {
    setRetryLoading(true);
    try {
      const correctedItems = retryItems.filter(item => item.corrected);
      
      if (correctedItems.length === 0) {
        window.alert('Nenhum item foi corrigido para tentar novamente.');
        return;
      }

      // Recarregar códigos existentes para garantir unicidade
      await loadExistingCodes();
      const codesInUse = new Set(usedCodes);

      const ensureUniqueCodSis = () => {
        let code = nextCodSis();
        while (codesInUse.has(code)) {
          code = nextCodSis();
        }
        codesInUse.add(code);
        return code;
      };

      // Preparar itens para retry
      const itemsToRetry = correctedItems.map(item => {
        let cod = String(item.cod_sis || '');
        // Se COD.SIS inválido (não 7 dígitos) ou já em uso, gerar novo
        if (!/^\d{7}$/.test(cod) || codesInUse.has(cod)) {
          cod = ensureUniqueCodSis();
        } else {
          codesInUse.add(cod);
        }

        return {
          cod_sis: cod,
        item_sis: item.item_sis || 1,
        data_nf: item.data_nf || '',
        nf: item.nf || '',
        item_nf: item.item_nf || 1,
        lote: item.lote || '',
        local_do_estoque: item.local_do_estoque || '',
        part_number: item.part_number || '',
        descricao: item.descricao || '',
        delivery: item.delivery || '',
        pedido_cliente: item.pedido_cliente || '',
        valor_unit: item.valor_unit || 0,
        valor_total: item.valor_total || 0,
        status: item.status || 'DISPONÍVEL EM ESTOQUE',
        os_relacionada: item.os_relacionada || '',
        localizacao_fisica: item.localizacao_fisica || 'Estoque',
        obs: item.obs || 'Cadastro manual via planilha (retry)',
        operador_id: operadorId
        };
      });

      // Tentar inserir os itens corrigidos
      const result = await itemsAPI.addItemsBulk(itemsToRetry);
      
      if (result.success) {
        const successCount = result.inserted || 0;
        const stillFailed = result.failed || [];
        
        if (stillFailed.length > 0) {
          // Ainda há itens que falharam, atualizar a lista
          setRetryItems(prev => prev.filter(item => 
            !stillFailed.some(failed => failed.cod_sis === item.cod_sis)
          ));
          window.alert(`✅ ${successCount} itens inseridos com sucesso!\n❌ ${stillFailed.length} itens ainda falharam.`);
        } else {
          // Todos os itens foram inseridos com sucesso
          closeRetryModal();
          await loadData();
          window.alert(`✅ Todos os ${successCount} itens foram inseridos com sucesso!`);
        }
      } else {
        window.alert('Erro ao tentar inserir itens corrigidos. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro no retry:', error);
      window.alert('Erro ao tentar inserir itens corrigidos: ' + error.message);
    } finally {
      setRetryLoading(false);
    }
  };

  const addLote = async () => { 
    const n = (novoLote || "").trim(); 
    if (!n) return; 
    if (lotes.some(l => (typeof l === 'object' ? l.nome : l) === n)) {
      window.alert('Este lote já existe!');
      return;
    }
    
    try {
      const data = await gestoresAPI.addLote(n, operadorId);
      
      if (data.success) {
        setLotes(s => [...s, { id: data.id, nome: n, operador_id: operadorId }]);
        setNovoLote("");
        window.alert('Lote criado com sucesso!');
      } else {
        window.alert('Erro ao criar lote: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao criar lote:', error);
      window.alert('Erro ao conectar com o servidor. Verifique se está rodando.');
    }
  };
  const removeLote = async () => { 
    if (!selLote) return; 
    
    // Confirmar exclusão
    if (!window.confirm(`Tem certeza que deseja excluir o lote "${selLote}"?`)) {
      return;
    }
    
    try {
      // Buscar ID do lote no banco
      const response = await fetch(`https://vega-solutions.site/api/gestores.php?type=lotes`);
      const data = await response.json();
      
      console.log('Resposta da API lotes:', data); // Debug
      
      if (response.ok && data.success) {
        const loteObj = data.data.find(l => l.nome === selLote);
        
        console.log('Lote encontrado:', loteObj); // Debug
        
        if (loteObj) {
          // Excluir do banco
          const deleteResponse = await fetch(`https://vega-solutions.site/api/gestores.php?type=lotes&id=${loteObj.id}`, {
            method: 'DELETE'
          });
          
          const deleteData = await deleteResponse.json();
          
          console.log('Resposta da exclusão:', deleteData); // Debug
          
          if (deleteResponse.ok && deleteData.success) {
            setLotes(s => s.filter(x => (typeof x === 'object' ? x.nome : x) !== selLote));
            setSelLote("");
            window.alert('Lote excluído com sucesso!');
          } else {
            window.alert('Erro ao excluir lote: ' + (deleteData.error || 'Erro desconhecido'));
          }
        } else {
          // Se não encontrou no banco, remove apenas da lista local
          setLotes(s => s.filter(x => (typeof x === 'object' ? x.nome : x) !== selLote));
          setSelLote("");
          window.alert('Lote removido da lista local.');
        }
      } else {
        window.alert('Erro ao buscar lote: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao excluir lote:', error);
      window.alert('Erro ao conectar com o servidor. Verifique se está rodando.');
    }
  };

  // Funções para gestão de estoques
  const estoquesFiltrados = useMemo(() => estoques.filter(l => {
    const nome = typeof l === 'object' ? l.nome : l;
    const estoqueOperadorId = typeof l === 'object' ? l.operador_id : null;
    
    // Se não for administrador, filtrar apenas estoques do mesmo operador ou estoques globais (operador_id null)
    if (currentUser?.cargo !== 'administrador' && operadorId) {
      if (estoqueOperadorId !== null && estoqueOperadorId !== operadorId) {
        return false;
      }
    }
    
    return String(nome || "").toLowerCase().includes(filtroEstoque.toLowerCase());
  }), [estoques, filtroEstoque, currentUser, operadorId]);
  const addEstoque = async () => { 
    const n = (novoEstoque || "").trim(); 
    if (!n) return; 
    if (estoques.some(e => (typeof e === 'object' ? e.nome : e) === n)) {
      window.alert('Este estoque já existe!');
      return;
    }
    
    try {
      const data = await gestoresAPI.addEstoque(n, operadorId);
      
      if (data.success) {
        setEstoques(s => [...s, { id: data.id, nome: n, operador_id: operadorId }]);
        setNovoEstoque("");
        window.alert('Estoque criado com sucesso!');
      } else {
        window.alert('Erro ao criar estoque: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao criar estoque:', error);
      window.alert('Erro ao conectar com o servidor. Verifique se está rodando.');
    }
  };
  const removeEstoque = async () => {
    if (!selEstoque) return;
    
    // Confirmar exclusão
    if (!window.confirm(`Tem certeza que deseja excluir o estoque "${selEstoque}"?`)) {
      return;
    }
    
    try {
      // Buscar ID do estoque no banco
      const response = await fetch(`https://vega-solutions.site/api/gestores.php?type=estoques`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        const estoqueObj = data.data.find(e => e.nome === selEstoque);
        
        if (estoqueObj) {
          // Excluir do banco
          const deleteResponse = await fetch(`https://vega-solutions.site/api/gestores.php?type=estoques&id=${estoqueObj.id}`, {
            method: 'DELETE'
          });
          
          const deleteData = await deleteResponse.json();
          
          if (deleteResponse.ok && deleteData.success) {
            setEstoques(s => s.filter(x => (typeof x === 'object' ? x.nome : x) !== selEstoque));
            setSelEstoque("");
            window.alert('Estoque excluído com sucesso!');
          } else {
            window.alert('Erro ao excluir estoque: ' + (deleteData.error || 'Erro desconhecido'));
          }
        } else {
          // Se não encontrou no banco, remove apenas da lista local
          setEstoques(s => s.filter(x => (typeof x === 'object' ? x.nome : x) !== selEstoque));
          setSelEstoque("");
          window.alert('Estoque removido da lista local.');
        }
      } else {
        window.alert('Erro ao buscar estoque: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao excluir estoque:', error);
      window.alert('Erro ao conectar com o servidor. Verifique se está rodando.');
    }
  };

  // Funções para gestão de bins
  const binsFiltrados = useMemo(() => bins.filter(b => {
    const nome = typeof b === 'object' ? b.nome : b;
    return String(nome || "").toLowerCase().includes(filtroBin.toLowerCase());
  }), [bins, filtroBin]);
  const addBin = async () => { 
    const n = (novoBin || "").trim(); 
    if (!n) return; 
    if (bins.some(b => (typeof b === 'object' ? b.nome : b) === n)) {
      window.alert('Este local já existe!');
      return;
    }
    
    try {
      const data = await gestoresAPI.addBin(n, operadorId);
      
      if (data.success) {
        setBins(s => [...s, { id: data.id, nome: n, operador_id: operadorId }]);
        setNovoBin("");
        window.alert('Local criado com sucesso!');
      } else {
        window.alert('Erro ao criar local: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao criar local:', error);
      window.alert('Erro ao conectar com o servidor. Verifique se está rodando.');
    }
  };
  const removeBin = async () => { 
    if (!selBin) return; 
    
    // Confirmar exclusão
    if (!window.confirm(`Tem certeza que deseja excluir o local "${selBin}"?`)) {
      return;
    }
    
    try {
      // Buscar ID do bin no banco
      const response = await fetch(`https://vega-solutions.site/api/gestores.php?type=bins`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        const binObj = data.data.find(b => b.nome === selBin);
        
        if (binObj) {
          // Excluir do banco
          const deleteResponse = await fetch(`https://vega-solutions.site/api/gestores.php?type=bins&id=${binObj.id}`, {
            method: 'DELETE'
          });
          
          const deleteData = await deleteResponse.json();
          
          if (deleteResponse.ok && deleteData.success) {
            setBins(s => s.filter(x => (typeof x === 'object' ? x.nome : x) !== selBin));
            setSelBin("");
            window.alert('Local excluído com sucesso!');
          } else {
            window.alert('Erro ao excluir local: ' + (deleteData.error || 'Erro desconhecido'));
          }
        } else {
          // Se não encontrou no banco, remove apenas da lista local
          setBins(s => s.filter(x => (typeof x === 'object' ? x.nome : x) !== selBin));
          setSelBin("");
          window.alert('Local removido da lista local.');
        }
      } else {
        window.alert('Erro ao buscar local: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao excluir local:', error);
      window.alert('Erro ao conectar com o servidor. Verifique se está rodando.');
    }
  };

  // Estados para controle de salvamento
  const [pendingUpdates, setPendingUpdates] = useState(new Map());
  const [updateTimeouts, setUpdateTimeouts] = useState(new Map());

  // Função para salvar alterações com debounce
  const saveItemChanges = async (itemId, changes) => {
    try {
      // Buscar o item atual
      const currentItem = items.find(item => item.id === itemId);
      if (!currentItem) return;

      // Aplicar as mudanças ao item
      const updatedItem = { ...currentItem, ...changes, ultima_alteracao: nowISO() };
      
      // Criar log das alterações
      const log = buildLog(currentItem, updatedItem, userName, 'Edição direta');
      
      // Preparar dados para envio (sem incluir campos que não devem ser atualizados)
      const updateData = {
        lote: changes.lote !== undefined ? changes.lote : currentItem.lote,
        local_do_estoque: changes.local_do_estoque !== undefined ? changes.local_do_estoque : currentItem.local_do_estoque,
        localizacao_fisica: changes.localizacao_fisica !== undefined ? changes.localizacao_fisica : currentItem.localizacao_fisica,
        status: changes.status !== undefined ? changes.status : currentItem.status,
        os_relacionada: changes.os_relacionada !== undefined ? changes.os_relacionada : currentItem.os_relacionada,
        nf_devolucao: changes.nf_devolucao !== undefined ? changes.nf_devolucao : currentItem.nf_devolucao,
        ultima_alteracao: updatedItem.ultima_alteracao,
        log: log // Enviar apenas o novo log, não o array completo
      };
      
      // Atualizar no banco de dados
      await itemsAPI.updateItem(itemId, updateData);
      
      // Atualizar estado local
      setItems(p => p.map(item => 
        item.id === itemId 
          ? { ...updatedItem, log: [...(item.log || []), log] }
          : item
      ));
      
      // Remover das atualizações pendentes
      setPendingUpdates(prev => {
        const newMap = new Map(prev);
        newMap.delete(itemId);
        return newMap;
      });
      
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      console.error('Detalhes do erro:', error.message);
      window.alert(`Erro ao salvar alteração no banco de dados: ${error.message}`);
    }
  };

  // Função para agendar salvamento com debounce (independente por item)
  const scheduleUpdate = (itemId, changes) => {
    // Cancelar timeout anterior se existir para este item específico
    const existingTimeout = updateTimeouts.get(itemId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    // Adicionar às atualizações pendentes para este item específico
    setPendingUpdates(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(itemId) || {};
      newMap.set(itemId, { ...existing, ...changes });
      return newMap;
    });
    
    // Agendar salvamento em 2 segundos APENAS para este item
    const timeout = setTimeout(() => {
      // Buscar as alterações pendentes atuais para este item específico
      setPendingUpdates(currentPending => {
        const pending = currentPending.get(itemId);
        if (pending) {
          console.log(`Salvando alterações para item ${itemId}:`, pending);
          saveItemChanges(itemId, pending);
        }
        return currentPending;
      });
    }, 2000);
    
    // Salvar o timeout específico para este item
    setUpdateTimeouts(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, timeout);
      return newMap;
    });
  };

  // Funções para edição direta de itens
  const setItemLote = (itemId, lote) => {
    const item = items.find(it => it.id === itemId);
    if (!item || (item.lote || '') === lote) return;
    
    console.log(`🔄 Item ${item.id} (${item.cod_sis}): Alterando lote para: ${lote}`);
    
    // Atualizar estado local imediatamente
    setItems(p => p.map((it) => {
      if (it.id !== itemId) return it;
      return { ...it, lote };
    }));
    
    // Agendar salvamento (timer independente para este item)
    scheduleUpdate(item.id, { lote });
  };

  const setItemEstoque = (itemId, loc) => {
    const item = items.find(it => it.id === itemId);
    if (!item || (item.localizacao_fisica || '') === loc) return;
    
    console.log(`🔄 Item ${item.id} (${item.cod_sis}): Alterando localização para: ${loc}`);
    
    // Atualizar estado local imediatamente
    setItems(p => p.map((it) => {
      if (it.id !== itemId) return it;
      return { ...it, localizacao_fisica: loc };
    }));
    
    // Agendar salvamento (timer independente para este item)
    scheduleUpdate(item.id, { localizacao_fisica: loc });
  };

  const setItemBin = (itemId, bin) => {
    const item = items.find(it => it.id === itemId);
    if (!item || (item.local_do_estoque || '') === bin) return;
    
    console.log(`🔄 Item ${item.id} (${item.cod_sis}): Alterando local do estoque para: ${bin}`);
    
    // Atualizar estado local imediatamente
    setItems(p => p.map((it) => {
      if (it.id !== itemId) return it;
      return { ...it, local_do_estoque: bin };
    }));
    
    // Agendar salvamento (timer independente para este item)
    scheduleUpdate(item.id, { local_do_estoque: bin });
  };

  // Função para salvar todas as alterações pendentes imediatamente
  const saveAllPendingUpdates = async () => {
    if (pendingUpdates.size === 0) return;
    
    console.log('Salvando todas as alterações pendentes:', pendingUpdates);
    
    for (const [itemId, changes] of pendingUpdates) {
      try {
        await saveItemChanges(itemId, changes);
      } catch (error) {
        console.error(`Erro ao salvar item ${itemId}:`, error);
      }
    }
  };

  // Salvar alterações pendentes antes de sair da página
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (pendingUpdates.size > 0) {
        e.preventDefault();
        e.returnValue = 'Há alterações não salvas. Deseja realmente sair?';
        return e.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && pendingUpdates.size > 0) {
        saveAllPendingUpdates();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      updateTimeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [pendingUpdates]);

  // Limpar timeouts quando o componente for desmontado
  useEffect(() => {
    return () => {
      updateTimeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Funções para movimentação
  const addPair = () => {
    const code = (pendingCode || '').trim();
    const os = (pendingOS || '').trim();
    if (!code) { window.alert('Bipe o COD.VEGA primeiro.'); return; }
    if (!os) { window.alert('Bipe a OS RELACIONADA.'); return; }
    setScanned(prev => (prev.includes(code) ? prev : [...prev, code]));
    setOsMap(prev => ({ ...prev, [code]: os }));
    setPendingCode(''); setPendingOS(''); codeInputRef.current?.focus();
  };

  const scannedInfo = useMemo(() => {
    const map = new Map(items.map(it => [it.cod_sis, it]));
    const found = []; const missing = [];
    for (const c of scanned) { const it = map.get(c); if (it) found.push(it); else missing.push(c); }
    return { found, missing };
  }, [scanned, items]);

  const confirmMove = async () => {
    if (!scannedInfo.found.length) { window.alert('Adicione pelo menos um par COD.VEGA + OS.'); return; }
    
    // Fechar modal de movimentação e abrir modal de assinatura
    setShowMove(false);
    setShowSignatureModal(true);
  };

  const handleSignatureConfirm = async (signatureData) => {
    try {
      setLoading(true);
      
      // Atualizar itens no estado local
      const updatedItems = items.map(it => {
        if (!scanned.includes(it.cod_sis)) return it;
        
        // Garantir que os valores sejam strings, não objetos
        const newLoc = (typeof moveLocal === 'object' ? moveLocal?.nome : moveLocal) || it.localizacao_fisica;
        const newLote = (typeof moveLote === 'object' ? moveLote?.nome : moveLote) !== undefined && (typeof moveLote === 'object' ? moveLote?.nome : moveLote) !== '' ? (typeof moveLote === 'object' ? moveLote?.nome : moveLote) : it.lote;
        const newStatus = moveStatus || it.status;
        const newBin = (typeof moveBin === 'object' ? moveBin?.nome : moveBin) !== undefined && (typeof moveBin === 'object' ? moveBin?.nome : moveBin) !== '' ? (typeof moveBin === 'object' ? moveBin?.nome : moveBin) : it.local_do_estoque;
        const newOS = ((osMap[it.cod_sis] ?? it.os_relacionada) || '');
        const newNfDevolucao = moveNfDevolucao || it.nf_devolucao || '';
        const movedAt = nowISO();
        const after = { ...it, localizacao_fisica: newLoc, lote: newLote, local_do_estoque: newBin, status: newStatus, os_relacionada: newOS, nf_devolucao: newNfDevolucao, ultima_alteracao: movedAt };
        const log = buildLog(it, after, userName, 'Movimentação');
        return { ...after, log: [...(it.log || []), log] };
      });
      
      // Salvar alterações no banco de dados
      const itemsToUpdate = updatedItems.filter(it => scanned.includes(it.cod_sis));
      const updatePromises = itemsToUpdate.map(item => {
        const updateData = {
          localizacao_fisica: item.localizacao_fisica,
        lote: item.lote,
        local_do_estoque: item.local_do_estoque,
        status: item.status,
        os_relacionada: item.os_relacionada,
          nf_devolucao: item.nf_devolucao,
          ultima_alteracao: item.ultima_alteracao,
          log: item.log,
          // Salvar signatureData completo em todos os itens da movimentação
          signature_data: JSON.stringify(signatureData)
        };
        return itemsAPI.updateItem(item.id, updateData);
      });
      
      await Promise.all(updatePromises);
      console.log(`${itemsToUpdate.length} itens movimentados e salvos no banco de dados!`);
      console.log('Dados da assinatura:', signatureData);
      
      // Atualizar estado local
      setItems(updatedItems);
      setShowSignatureModal(false); 
      setScanned([]); 
      setOsMap({}); 
      setPendingCode(''); 
      setPendingOS(''); 
      setMoveStatus('');
      setMoveNfDevolucao('');
      setMoveLocal('');
      setMoveLote('');
      setMoveBin('');
      
      window.alert(`Movimentação confirmada para ${itemsToUpdate.length} item(s) com assinaturas coletadas!`);
      // Recarregar a página após confirmação de movimentação
      window.location.reload();
      
    } catch (error) {
      console.error('Erro ao salvar movimentação no banco:', error);
      window.alert(`Erro ao salvar movimentação no banco de dados: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const viewSignature = (item) => {
    if (item.signature_data) {
      try {
        const signatureData = JSON.parse(item.signature_data);
        setSelectedItemSignature({ signatureData, itemData: item });
        setShowSignatureViewer(true);
      } catch (error) {
        console.error('Erro ao parsear dados de assinatura:', error);
        alert('Erro ao carregar assinatura. Dados corrompidos.');
      }
    } else {
      alert('Este item não possui assinatura de entrega.');
    }
  };


  const closeSignatureModal = () => {
    setShowSignatureModal(false);
    // Limpar todos os dados de movimentação
    setScanned([]);
    setOsMap({});
    setPendingCode('');
    setPendingOS('');
    setMoveStatus('');
    setMoveNfDevolucao('');
    setMoveLocal('');
    setMoveLote('');
    setMoveBin('');
  };

  // Funções para upload de XML
  const openPickerXML = () => fileXMLRef.current?.click();

  const onFilesXML = async (files) => {
    if (!files?.length) return;
    const created = [];
    
    // Verificar chNFe antes de processar
    for (const f of Array.from(files)) {
      const xmlText = await f.text();
      try {
        const parsed = parseNFeXMLBrowser(xmlText);
        
        // Verificar se chNFe já existe no banco ANTES de processar
        if (parsed.chNFe) {
          try {
            const checkResponse = await fetch(`${API_BASE_URL}/items.php?check_chnfe=${encodeURIComponent(parsed.chNFe)}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'X-Empresa-ID': operadorId
              }
            });
            
            if (checkResponse.ok) {
              const checkResult = await checkResponse.json();
              if (checkResult.exists) {
                window.alert(`❌ Chave de NF já usada!\n\nChNFe: ${parsed.chNFe}\nNF: ${parsed.nfNumber}\n\nEsta nota fiscal já foi cadastrada no sistema.`);
                return; // Para o processamento
              }
            }
          } catch (error) {
            console.error('Erro ao verificar chNFe:', error);
            // Continua o processamento mesmo se a verificação falhar
          }
        }
        for (const it of parsed.items) {
          const qty = Math.max(1, Math.round(Number(it.qCom || 1)));
          const vUnit = Number(it.vUnCom ?? 0) || 0;
          const vProd = Number(it.vProd ?? 0) || 0;
          const valorUnit = vUnit > 0 ? vUnit : (vProd > 0 && qty > 0 ? vProd / qty : 0);
          
          console.log(`Processing item: ${it.cProd}, qty: ${qty}`);
          
          for (let i = 0; i < qty; i++) {
            // Gerar código único para cada unidade
            const codSis = await getOrCreateCodSis(it.cProd, parsed.nfNumber);
            
            const base = {
              cod_sis: codSis,
              data_nf: parsed.issueDate,
              nf: parsed.nfNumber,
              item_nf: qty, // Quantidade total do item na NF
              item_sis: 1, // Sempre 1 para cada unidade (representa quantidade individual)
              lote: "",
              local_do_estoque: "",
              part_number: it.cProd,
              descricao: it.xProd,
              delivery: parsed.deliveryCode || "",
              pedido_cliente: parsed.pedidoCode || "",
              valor_unit: valorUnit,
              valor_total: valorUnit,
              status: "DISPONÍVEL EM ESTOQUE",
              os_relacionada: "",
              etiqueta_gerada: 0,
              localizacao_fisica: "Estoque",
              obs: `Importado da NF-e ${parsed.nfNumber} - Unidade ${i + 1} de ${qty}`,
              ultima_alteracao: nowISO(),
              chnfe: parsed.chNFe || "", // Todas as unidades têm a mesma chNFe
            };
            base.log = [buildLog({ localizacao_fisica: '-', local_do_estoque: '', lote: '', status: '-', os_relacionada: '' }, base, userName, 'Entrada')];
            created.push(base);
            console.log(`Created item ${i + 1}/${qty}: ${codSis} (item_sis: 1, part_number: ${it.cProd})`);
          }
        }
        
        console.log(`Total items created: ${created.length}`);
      } catch (e) { window.alert(`Falha ao ler ${f.name}: ${e.message}`); }
    }
    if (created.length) { setPendingCreated(created); setAskLabels(true); }
  };

  // Função para salvar itens no banco de dados
  const saveItemsToDatabase = async (items) => {
    try {
      setLoading(true);
      
      // Carregar códigos existentes se necessário
      await loadExistingCodes();
      
      // Verificar se os itens já têm códigos válidos
      const itemsWithValidCodes = items.map(item => {
        // Se já tem código válido, manter
        if (item.cod_sis && item.cod_sis.length === 7 && /^\d{7}$/.test(item.cod_sis)) {
          return item;
        }
        // Se não tem código válido, gerar novo aleatório
        return {
          ...item,
          cod_sis: nextCodSis(),
          operador_id: operadorId
        };
      });
      
      // Enviar em lotes para evitar muitas conexões simultâneas
      const BATCH_SIZE = 50;
      let totalInserted = 0;
      let totalFailed = 0;
      const failedItems = [];
      
      for (let i = 0; i < itemsWithValidCodes.length; i += BATCH_SIZE) {
        const batch = itemsWithValidCodes.slice(i, i + BATCH_SIZE);
        console.log(`Sending batch ${Math.floor(i/BATCH_SIZE) + 1}:`, batch.map(item => ({
          cod_sis: item.cod_sis,
          part_number: item.part_number,
          chnfe: item.chnfe,
          item_sis: item.item_sis,
          item_nf: item.item_nf
        })));
        try {
          const result = await itemsAPI.addItemsBulk(batch);
          console.log(`Batch result:`, result);
          totalInserted += result.inserted || 0;
          
          if (result.failed && result.failed.length > 0) {
            totalFailed += result.failed.length;
            failedItems.push(...result.failed);
          }
        } catch (error) {
          console.error('Erro no lote:', error);
          totalFailed += batch.length;
          failedItems.push(...batch.map((item, index) => ({
            index: i + index,
            cod_sis: item.cod_sis,
            error: error.message
          })));
        }
      }
      
      // Atualizar códigos usados com os novos códigos gerados
      const newCodes = itemsWithValidCodes.map(item => item.cod_sis);
      setUsedCodes(prev => new Set([...prev, ...newCodes]));
      
      // Exibir resultado do upload
      if (totalFailed > 0) {
        // Abrir modal de retry para itens falhados
        openRetryModal(failedItems);
        
        // Mostrar resumo inicial
        const chnfeErrors = failedItems.filter(f => f.error && f.error.includes('Nota já cadastrada'));
        if (chnfeErrors.length > 0) {
          window.alert(`⚠️ Upload concluído com avisos:\n\n✅ ${totalInserted} itens inseridos com sucesso\n❌ ${totalFailed} itens falharam\n\n📋 Notas já cadastradas: ${chnfeErrors.length}\n\nUm modal será aberto para corrigir os itens falhados.`);
        } else {
          window.alert(`⚠️ Upload concluído com avisos:\n\n✅ ${totalInserted} itens inseridos com sucesso\n❌ ${totalFailed} itens falharam\n\nUm modal será aberto para corrigir os itens falhados.`);
        }
      } else {
        window.alert(`✅ Upload concluído com sucesso!\n\n${totalInserted} itens inseridos no banco de dados.`);
      }
      
      console.log(`${totalInserted} itens salvos no banco de dados com sucesso!`);
      console.log(`${totalFailed} itens falharam`);
      if (failedItems.length > 0) {
        console.log('Itens que falharam:', failedItems);
      }
    } catch (error) {
      console.error('Erro ao salvar itens no banco:', error);
      window.alert(`Erro ao salvar itens no banco de dados: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmWithoutLabels = async () => { 
    if (!window.confirm('TEM CERTEZA QUE DESEJA DAR ENTRADA SEM GERAR ETIQUETA?')) return; 
    
    const itemsToSave = createdOrEmpty(pendingCreated);
    
    // Salvar no banco de dados
    await saveItemsToDatabase(itemsToSave);
    
    // Atualizar estado local
    setItems(p => [...itemsToSave, ...p]); 
    setPendingCreated([]); 
    setAskLabels(false); 
    // Recarregar a página após upload XML sem etiquetas
    window.location.reload();
  };
  
  const confirmWithLabels = async () => { 
    openLabelsWindow(pendingCreated); 
    
    const itemsToSave = createdOrEmpty(pendingCreated);
    
    // Salvar no banco de dados
    await saveItemsToDatabase(itemsToSave);
    
    // Atualizar estado local
    setItems(p => [...itemsToSave, ...p]); 
    setPendingCreated([]); 
    setAskLabels(false); 
    // Recarregar a página após upload XML com etiquetas
    window.location.reload();
  };
  function createdOrEmpty(arr) { return Array.isArray(arr) ? arr : []; }

  // Funções para upload manual
  const downloadManualTemplateCSV = () => {
    const sep = ';';
    const headers = MANUAL_FIELDS;
    
    // Criar linha de exemplo com status pré-definido
    const exampleRow = headers.map(header => {
      if (header === 'STATUS') {
        return '"DISPONIVEL PARA RETIRADA"';
      }
      return '""';
    });
    
    const csv = [
      "\uFEFF" + headers.map(h => `"${h}"`).join(sep),
      exampleRow.join(sep)
    ];
    
    const blob = new Blob(csv, { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cadastro_manual_estoque_modelo.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  
  const onManualUpload = async (files) => {
    if (!files?.length) return;
    const file = files[0];
    const text = await file.text();
    const newItems = [];
    const headers = MANUAL_FIELDS.map(h => h.toUpperCase().trim());
    
    const rows = text.split(/\r?\n/).filter(line => line.trim());
    if (rows.length === 0) {
      window.alert("O arquivo está vazio.");
      return;
    }
    
    const uploadedHeaders = rows[0].split(';').map(h => h.trim().replace(/"/g, '').toUpperCase());
    
    const missingHeaders = headers.filter(h => !uploadedHeaders.includes(h));
    if (missingHeaders.length > 0) {
      window.alert(`O arquivo não contém as colunas obrigatórias: ${missingHeaders.join(', ')}`);
      return;
    }
    
    const dataRows = rows.slice(1);
    
    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      const cols = row.split(';');
      const item = {};
      
      uploadedHeaders.forEach((header, index) => {
        item[header] = (cols[index] || '').trim().replace(/"/g, '');
      });
      
      const partNumber = item["PART NUMBER (COD. PEÇA)"];
      const nf = item["NF"];
      if (!partNumber || !nf) {
        console.warn(`Linha ${i + 2} ignorada por falta de dados essenciais.`);
        continue;
      }
      
      const valorUnit = Number(item["VALOR UNIT."]?.replace(',', '.') || 0);
      const itemNfCount = Math.max(1, parseInt(item["ITEM NF"], 10) || 1);
      
      for (let j = 0; j < itemNfCount; j++) {
        // Gerar código único para cada unidade da peça
        const codSis = await getOrCreateCodSis(partNumber, nf);
        
        const newItem = {
          cod_sis: codSis,
          data_nf: item["DATA NF"] || '',
          nf: nf,
          item_nf: itemNfCount,
          lote: item["LOTE"] || '',
          local_do_estoque: '',
          part_number: partNumber,
          descricao: item["DESCRIÇÃO DA PEÇA"] || '',
          delivery: item["DELIVERY"] || '',
          pedido_cliente: item["Nº PEDIDO"] || '',
          valor_unit: valorUnit,
          valor_total: valorUnit,
          status: item["STATUS"] || "DISPONÍVEL EM ESTOQUE",
          os_relacionada: item["OS RELACIONADA"] || '',
          etiqueta_gerada: 0,
          localizacao_fisica: item["LOCALIZAÇÃO FÍSICA"] || "Estoque",
          obs: `Cadastro manual via planilha`,
          ultima_alteracao: nowISO(),
          item_sis: 1,
        };
        newItem.log = [buildLog({ localizacao_fisica: '-', local_do_estoque: '', lote: '', status: '-', os_relacionada: '' }, newItem, userName, 'Entrada Manual')];
        newItems.push(newItem);
      }
    }
    
    if (newItems.length > 0) {
      try {
        setLoading(true);
        
        // Carregar códigos existentes se necessário
        await loadExistingCodes();
        
        // Verificar se os itens já têm códigos válidos
        const itemsWithValidCodes = newItems.map(item => {
          // Se já tem código válido, manter
          if (item.cod_sis && item.cod_sis.length === 7 && /^\d{7}$/.test(item.cod_sis)) {
            return item;
          }
          // Se não tem código válido, gerar novo aleatório
          return {
            ...item,
            cod_sis: nextCodSis(),
            operador_id: operadorId
          };
        });
        
        // Salvar no banco de dados em lotes para evitar excesso de conexões
        await saveItemsToDatabase(itemsWithValidCodes);
        
        // Atualizar códigos usados com os novos códigos gerados
        const newCodes = itemsWithValidCodes.map(item => item.cod_sis);
        setUsedCodes(prev => new Set([...prev, ...newCodes]));
        
        // Atualizar estado local
        setItems(prev => [...prev, ...itemsWithValidCodes]);
        window.alert(`${itemsWithValidCodes.length} itens foram cadastrados e salvos no banco de dados com sucesso!`);
        console.log(`Códigos únicos gerados: ${newCodes.join(', ')}`);
      } catch (error) {
        console.error('Erro ao salvar itens no banco:', error);
        window.alert(`Erro ao salvar itens no banco de dados: ${error.message}`);
      } finally {
        setLoading(false);
      }
    } else {
      window.alert("Nenhum item válido encontrado para cadastro.");
    }
    setShowManualImport(false);
  };

  const downloadViewCSV = () => {
    downloadCSV(filtered, `estoque_visualizacao_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando dados...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Erro ao carregar dados</h3>
        <p>{error}</p>
        <button onClick={loadData}>Tentar novamente</button>
              </div>
    );
                    }
                    
                    return (
    <div className="gestores-page">
      <TailwindCSS />
      <ResponsiveStyles />
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">VEGA — Visão de Estoque e Gestão Avançada</h1>
        
        
        <div className="header-actions"> 
          <input ref={fileXMLRef} type="file" accept=".xml" multiple hidden onChange={(e) => onFilesXML(e.target.files)} />
          <input ref={fileManualRef} type="file" accept=".csv" hidden onChange={(e) => onManualUpload(e.target.files)} />
          
          <div className="action-group">
            <button 
              className="btn btn-warning" 
              onClick={() => setShowMove(true)}
              style={{ fontSize: '18px', padding: '12px 48px', fontWeight: '700' }}
            >
              🔄 Movimentar
            </button>
          </div>
          
          <div className="action-group">
            <button 
              className="btn btn-primary" 
              onClick={openPickerXML}
            >
              📄 Upload XML
            </button>
            {isAdmin() && (
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowManualImport(true)}
              >
                📝 Cadastro Manual
              </button>
            )}
          </div>
          
          <div className="action-group">
            <button 
              className="btn btn-info" 
              onClick={downloadViewCSV}
            >
              📊 Baixar CSV
            </button>
            <button 
              className="btn btn-success" 
              onClick={() => setShowGenPN(true)}
            >
              🏷️ Gerar Etiquetas
            </button>
          </div>
          
          {pendingUpdates.size > 0 && (
            <div className="action-group pending-group">
              <button 
                className="btn btn-warning" 
                onClick={saveAllPendingUpdates}
              >
                💾 Salvar {pendingUpdates.size} item(s)
              </button>
            </div>
          )}
          
          {selectedItems.size > 0 && (
            <div className="action-group pending-group">
              <button 
                className="btn btn-danger" 
                onClick={handleBulkDelete}
              >
                🗑️ Excluir {selectedItems.size} item(s)
              </button>
            </div>
          )}
        </div>
      </div>
                
      {/* Gestores */}
      <div className="gestores-grid">
        {/* Criar LOTE - Apenas para Administradores */}
        {isAdmin() && (
          <div className="gestor-card" style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}>
            <div className="card-header">
              <h3>📦 Criar LOTE</h3>
              <p>Gerencie os lotes de estoque</p>
            </div>
            <div className="card-content">
              <div className="input-group">
                <input className="form-input" placeholder="Nome do lote" value={novoLote} onChange={(e) => setNovoLote(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={addLote}>+ Adicionar</button>
              </div>
              <input className="filter-input" placeholder="Filtrar lotes" value={filtroLote} onChange={(e) => setFiltroLote(e.target.value)} />
              <select className="form-select" size={6} value={selLote} onChange={(e) => setSelLote(e.target.value)}>
                {lotesFiltrados.map(l => <option key={typeof l === 'object' ? l.id : l} value={typeof l === 'object' ? l.nome : l}>{typeof l === 'object' ? l.nome : l}</option>)}
              </select>
              <div className="card-actions">
                <button className="btn btn-danger btn-sm" disabled={!selLote} onClick={removeLote}>🗑️ Remover selecionado</button>
              </div>
            </div>
          </div>
        )}
            
        {/* Criar Estoque Atual - Apenas para Administradores */}
        {isAdmin() && (
          <div className="gestor-card" style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}>
            <div className="card-header">
              <h3>🏪 Criar Estoque Atual</h3>
              <p>Gerencie os locais de estoque</p>
            </div>
            <div className="card-content">
              <div className="input-group">
                <input className="form-input" placeholder="Nome do estoque" value={novoEstoque} onChange={(e) => setNovoEstoque(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={addEstoque}>+ Adicionar</button>
              </div>
              <input className="filter-input" placeholder="Filtrar estoques" value={filtroEstoque} onChange={(e) => setFiltroEstoque(e.target.value)} />
              <select className="form-select" size={6} value={selEstoque} onChange={(e) => setSelEstoque(e.target.value)}>
                {estoquesFiltrados.map(l => <option key={typeof l === 'object' ? l.id : l} value={typeof l === 'object' ? l.nome : l}>{typeof l === 'object' ? l.nome : l}</option>)}
              </select>
              <div className="card-actions">
                <button className="btn btn-danger btn-sm" disabled={!selEstoque} onClick={removeEstoque}>🗑️ Remover selecionado</button>
              </div>
            </div>
          </div>
        )}

        {/* Criar Local do estoque (bins) - Apenas para Administradores */}
        {isAdmin() && (
          <div className="gestor-card" style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}>
            <div className="card-header">
              <h3>📋 Criar Local do estoque (bins)</h3>
              <p>Gerencie os locais internos</p>
            </div>
            <div className="card-content">
              <div className="input-group">
                <input className="form-input" placeholder="Ex.: Gôndula 2 / Bin B3" value={novoBin} onChange={(e) => setNovoBin(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={addBin}>+ Adicionar</button>
              </div>
              <input className="filter-input" placeholder="🔍 Filtrar locais internos" value={filtroBin} onChange={(e) => setFiltroBin(e.target.value)} />
              <select className="form-select" size={6} value={selBin} onChange={(e) => setSelBin(e.target.value)}>
                {binsFiltrados.map(b => <option key={typeof b === 'object' ? b.id : b} value={typeof b === 'object' ? b.nome : b}>{typeof b === 'object' ? b.nome : b}</option>)}
              </select>
              <div className="card-actions">
                <button className="btn btn-danger btn-sm" disabled={!selBin} onClick={removeBin}>🗑️ Remover selecionado</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filtros principais */}
      <div className="filters-section" style={{ animation: 'fadeInUp 0.6s ease-out 0.4s both' }}>
        <div className="section-header">
          <h3>🔍 Filtros Avançados</h3>
          <p>Filtre os itens por diferentes critérios</p>
        </div>
        <div className="filters-grid">
          <div className="search-input">
            <input className="filter-input" placeholder="🔍 Pesquisar (COD, NF, PN, Descrição, Delivery)" value={fQ} onChange={(e) => setFQ(e.target.value)} />
          </div>
          <input className="filter-input" placeholder="📄 NF" value={fNF} onChange={(e) => setFNF(e.target.value)} />
          <input className="filter-input" placeholder="🔢 PART NUMBER" value={fPN} onChange={(e) => setFPN(e.target.value)} />
          <div className="description-input">
            <input className="filter-input" placeholder="📝 DESCRIÇÃO" value={fDesc} onChange={(e) => setFDesc(e.target.value)} />
          </div>
          <input className="filter-input" placeholder="🚚 DELIVERY" value={fDelivery} onChange={(e) => setFDelivery(e.target.value)} />
          <input className="filter-input" placeholder="🔧 OS RELACIONADA" value={fOS} onChange={(e) => setFOS(e.target.value)} />
          <select className="filter-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
            <option value="">📊 Status (todos)</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="filter-select" value={fLocal} onChange={(e) => setFLocal(e.target.value)}>
            <option value="">Estoque Atual (todos)</option>
            {estoques.map(l => <option key={typeof l === 'object' ? l.id : l} value={typeof l === 'object' ? l.nome : l}>{typeof l === 'object' ? l.nome : l}</option>)}
          </select>
          <select className="filter-select" value={fLote} onChange={(e) => setFLote(e.target.value)}>
            <option value="">Lote (todos)</option>
            {lotes.map(l => <option key={typeof l === 'object' ? l.id : l} value={typeof l === 'object' ? l.nome : l}>{typeof l === 'object' ? l.nome : l}</option>)}
          </select>
          <div className="date-filters-row">
            <div className="date-filter">
              <label className="date-label">📅 Data NF — de</label>
              <input type="date" className="date-input" value={fDataNFDe} onChange={(e) => setFDataNFDe(e.target.value)} />
            </div>
            <div className="date-filter">
              <label className="date-label">📅 Data NF — até</label>
              <input type="date" className="date-input" value={fDataNFAt} onChange={(e) => setFDataNFAt(e.target.value)} />
            </div>
            <div className="date-filter">
              <label className="date-label">⏰ Última alteração — de</label>
              <input type="date" className="date-input" value={fUltAltDe} onChange={(e) => setFUltAltDe(e.target.value)} />
            </div>
            <div className="date-filter">
              <label className="date-label">⏰ Última alteração — até</label>
              <input type="date" className="date-input" value={fUltAltAt} onChange={(e) => setFUltAltAt(e.target.value)} />
            </div>
          </div>
          <div className="clear-filters">
            <button className="btn btn-outline clear-btn" onClick={() => { setFQ(''); setFNF(''); setFPN(''); setFDesc(''); setFDelivery(''); setFStatus(''); setFOS(''); setFLocal(''); setFLote(''); setFDataNFDe(''); setFDataNFAt(''); setFUltAltDe(''); setFUltAltAt(''); }}>🧹 Limpar filtros</button>
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="summary-section" style={{ animation: 'fadeInUp 0.6s ease-out 0.5s both' }}>
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-icon">📦</div>
            <div className="summary-content">
              <div className="summary-label">Unidades (ITEM VEGA)</div>
              <div className="summary-value">{totalUnidades}</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <div className="summary-label">Valor total</div>
              <div className="summary-value">{brl(valorTotal)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="table-section" style={{ animation: 'fadeInUp 0.6s ease-out 0.6s both' }}>
        <div className="table-container">
          <table className="data-table">
            <thead className="table-header">
              <tr>
                <th>
                  <input 
                    type="checkbox" 
                    checked={selectAll}
                    onChange={handleSelectAll}
                    title="Selecionar todos"
                  />
                </th>
                {['🖨️', 'COD. VEGA', 'ITEM VEGA', 'DATA NF', 'NF', 'ITEM NF', 'LOTE', 'LOCAL DO ESTOQUE', 'PART NUMBER (COD. PEÇA)', 'DESCRIÇÃO DA PEÇA', 'Nº PEDIDO', 'DELIVERY', 'VALOR UNIT.', 'VALOR TOTAL', 'STATUS', 'OS RELACIONADA', 'LOCALIZAÇÃO FÍSICA', 'NF DEVOLUÇÃO', 'ÚLTIMA ALTERAÇÃO', 'ASSINATURA', 'Ações'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
                    </thead>
            <tbody className="table-body">
              {paginatedItems.map((it, idx) => (
                  <tr 
                    key={it.cod_sis} 
                    className="table-row"
                    style={{ 
                      animation: `fadeInUp 0.4s ease-out ${idx * 0.05}s both`,
                      animationDelay: `${idx * 0.05}s`
                    }}
                  >
                  {/* Checkbox de seleção */}
                  <td>
                    <input 
                      type="checkbox" 
                      checked={selectedItems.has(it.id)}
                      onChange={() => handleSelectItem(it.id)}
                      title="Selecionar item"
                    />
                  </td>
                  {/* Impressora manual */}
                  <td className="action-cell"><button className="btn btn-sm btn-icon" title="Imprimir etiqueta" onClick={() => openLabelsWindow([it])}>🖨️</button></td>
                  <td className="code-cell">{it.cod_sis || ''}</td>
                  <td className="number-cell">{it.item_sis ?? 1}</td>
                  <td className="date-cell">{it.data_nf || ''}</td>
                  <td className="number-cell">{it.nf || ''}</td>
                  <td className="number-cell">{it.item_nf ?? ''}</td>
                  <td className="select-cell">
                    <select className="table-select" value={it.lote || ''} onChange={(e) => setItemLote(it.id, e.target.value)}>
                      <option value="">(sem lote)</option>
                      {lotes.map(l => <option key={typeof l === 'object' ? l.id : l} value={typeof l === 'object' ? l.nome : l}>{typeof l === 'object' ? l.nome : l}</option>)}
                    </select>
                  </td>
                  <td className="select-cell">
                    <select className="table-select" value={it.local_do_estoque || ''} onChange={(e) => setItemBin(it.id, e.target.value)}>
                      <option value="">(não definido)</option>
                      {bins.map(b => <option key={typeof b === 'object' ? b.id : b} value={typeof b === 'object' ? b.nome : b}>{typeof b === 'object' ? b.nome : b}</option>)}
                    </select>
                  </td>
                  <td className="part-number-cell">{it.part_number || ''}</td>
                  <td className="description-cell" title={it.descricao}>{it.descricao || ''}</td>
                  <td className="number-cell">{it.pedido_cliente || ''}</td>
                  <td className="number-cell">{it.delivery || ''}</td>
                  <td className="value-cell">{brl(it.valor_unit)}</td>
                  <td className="value-cell">{brl(it.valor_total)}</td>
                  <td className="status-cell">{it.status || ''}</td>
                  <td className="number-cell">{it.os_relacionada || ''}</td>
                  <td className="select-cell">
                    <select className="table-select" value={it.localizacao_fisica || ''} onChange={(e) => setItemEstoque(it.id, e.target.value)}>
                      <option value="">(sem localização)</option>
                      {estoques.map(l => <option key={typeof l === 'object' ? l.id : l} value={typeof l === 'object' ? l.nome : l}>{typeof l === 'object' ? l.nome : l}</option>)}
                    </select>
                          </td>
                  <td className="number-cell">{it.nf_devolucao || ''}</td>
                  <td className="date-cell">{it.ultima_alteracao || ''}</td>
                  <td className="action-cell">
                    {it.signature_data ? (
                      <button 
                        className="btn btn-sm btn-success" 
                        onClick={() => viewSignature(it)}
                        title="Ver Assinatura"
                      >
                        ✍️
                      </button>
                    ) : (
                      <span className="no-signature">—</span>
                    )}
                  </td>
                  <td className="log-cell">
                    <details className="log-details">
                      <summary className="log-summary">📋 LOG</summary>
                      <div className="log-content">
                        {(it.log && it.log.length) ? it.log.map((entry, i) => (
                          <div key={i} className="log-entry">{entry}</div>
                        )) : '—'}
                      </div>
                    </details>
                          </td>
                        </tr>
                      ))}
                    </tbody>
          </table>
        </div>
        
        {/* Controles de Paginação */}
        {totalPages > 1 && (
          <div className="pagination-section" style={{ animation: 'fadeInUp 0.6s ease-out 0.7s both' }}>
              <div className="pagination-info">
                <span className="pagination-text">
                Mostrando {startIndex + 1} a {Math.min(endIndex, filtered.length)} de {filtered.length} registros
                </span>
              </div>
              <div className="pagination-controls">
                <button
                className="btn btn-sm btn-outline" 
                onClick={() => setCurrentPage(1)} 
                  disabled={currentPage === 1}
                title="Primeira página"
              >
                ⏮️
              </button>
              <button 
                className="btn btn-sm btn-outline" 
                onClick={() => setCurrentPage(currentPage - 1)} 
                  disabled={currentPage === 1}
                title="Página anterior"
                >
                ⏪
                </button>
                
                <div className="pagination-numbers">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                      className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                className="btn btn-sm btn-outline" 
                onClick={() => setCurrentPage(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                title="Próxima página"
              >
                ⏩
              </button>
              <button 
                className="btn btn-sm btn-outline" 
                onClick={() => setCurrentPage(totalPages)} 
                  disabled={currentPage === totalPages}
                title="Última página"
                >
                ⏭️
                </button>
              </div>
            
            <div className="pagination-jump">
              <span className="pagination-text">Ir para:</span>
              <input 
                type="number" 
                min="1" 
                max={totalPages} 
                value={currentPage} 
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= totalPages) {
                    setCurrentPage(page);
                  }
                }}
                className="pagination-input"
                title={`Página 1 a ${totalPages}`}
              />
            </div>
          </div>
        )}
            </div>
            
      {/* Modal de Movimentação */}
      {showMove && (
        <div className="modal-overlay">
          <div className="modal modal-wide">
            <div className="modal-header">
              <h3>🔄 Movimentar Itens</h3>
              <button className="btn btn-sm btn-outline" onClick={() => setShowMove(false)}>✕ Fechar</button>
            </div>
            <div className="modal-body">
              <div className="config-section">
                <h3>⚙️ Configuração de Destino</h3>
                <div className="config-grid-horizontal">
                  <div className="config-group">
                    <label>🏪 LOCAL DO ESTOQUE (bins)</label>
                    <select className="filter-select" value={moveBin} onChange={(e) => setMoveBin(e.target.value)}>
                      <option value="">(manter)</option>
                      {bins.map(b => <option key={typeof b === 'object' ? b.id : b} value={typeof b === 'object' ? b.nome : b}>{typeof b === 'object' ? b.nome : b}</option>)}
                    </select>
                  </div>
                  <div className="config-group">
                    <label>📍 LOCALIZAÇÃO FÍSICA</label>
                    <select className="filter-select" value={moveLocal} onChange={(e) => setMoveLocal(e.target.value)}>
                      <option value="">(manter atual)</option>
                      {estoques.map(l => <option key={typeof l === 'object' ? l.id : l} value={typeof l === 'object' ? l.nome : l}>{typeof l === 'object' ? l.nome : l}</option>)}
                    </select>
                  </div>
                  <div className="config-group">
                    <label>📦 LOTE</label>
                    <select className="filter-select" value={moveLote} onChange={(e) => setMoveLote(e.target.value)}>
                      <option value="">(sem/inalterado)</option>
                      {lotes.map(l => <option key={typeof l === 'object' ? l.id : l} value={typeof l === 'object' ? l.nome : l}>{typeof l === 'object' ? l.nome : l}</option>)}
                    </select>
                  </div>
                  <div className="config-group">
                    <label>📊 STATUS</label>
                    <select className="filter-select" value={moveStatus} onChange={(e) => setMoveStatus(e.target.value)}>
                      <option value="">(manter)</option>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="config-group">
                    <label>📄 NÚMERO DA NOTA DE DEVOLUÇÃO</label>
                    <input 
                      className="filter-input" 
                      type="text"
                      placeholder="Ex.: 123456"
                      value={moveNfDevolucao} 
                      onChange={(e) => setMoveNfDevolucao(e.target.value)} 
                    />
                  </div>
                </div>
              </div>
              
              <div className="scanner-section">
                <h3>📱 Scanner de Códigos</h3>
                <div className="scanner-input-horizontal">
                  <div className="config-group">
                    <label>1º BIPE — COD.VEGA</label>
                    <input 
                      ref={codeInputRef} 
                      className="filter-input" 
                      placeholder="Ex.: 10000" 
                      value={pendingCode} 
                      onChange={(e) => setPendingCode(e.target.value)} 
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); osInputRef.current?.focus(); } }} 
                    />
                  </div>
                  <div className="config-group">
                    <label>2º BIPE — OS RELACIONADA</label>
                    <input 
                      ref={osInputRef} 
                      className="filter-input" 
                      placeholder="Ex.: 4177009911" 
                      value={pendingOS} 
                      onChange={(e) => setPendingOS(e.target.value)} 
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPair(); } }} 
                    />
                  </div>
                  <div className="scanner-actions">
                    <button className="btn btn-primary" onClick={addPair} disabled={!pendingCode || !pendingOS}>➕ Adicionar</button>
                    <button className="btn btn-outline" onClick={() => { setScanned([]); setOsMap({}); setPendingCode(''); setPendingOS(''); }}>🧹 Limpar</button>
                  </div>
                </div>
              </div>

              <div className="results-section">
                <div className="results-card">
                  <div className="card-header">
                    <h4>✅ Encontrados ({scannedInfo.found.length})</h4>
                  </div>
                  <div className="items-table">
                    <table>
                      <thead>
                        <tr>
                          <th>COD.VEGA</th>
                          <th>PN</th>
                          <th>Local Atual</th>
                          <th>Local Novo</th>
                          <th>OS (Atual)</th>
                          <th>OS (Nova)</th>
                          <th>Lote Atual</th>
                          <th>Lote Novo</th>
                          <th>Status Atual</th>
                          <th>Status Novo</th>
                          <th>NF Devolução</th>
                          <th>Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scannedInfo.found.map(it => (
                          <tr key={it.cod_sis}>
                            <td className="item-code">{it.cod_sis || ''}</td>
                            <td>{it.part_number || ''}</td>
                            <td>{it.localizacao_fisica || '-'}</td>
                            <td>
                              <select 
                                className="form-select" 
                                value={(typeof moveLocal === 'object' ? moveLocal?.nome : moveLocal) || it.localizacao_fisica || ''} 
                                onChange={(e) => setMoveLocal(e.target.value)}
                              >
                                <option value="">Selecionar Local</option>
                                {locais.map(local => (
                                  <option key={local} value={local}>{local}</option>
                                ))}
                              </select>
                            </td>
                            <td>{it.os_relacionada || '-'}</td>
                            <td>
                              <input 
                                className="os-input" 
                                value={((osMap[it.cod_sis] ?? it.os_relacionada) ?? '')} 
                                onChange={(e) => setOsMap(p => ({ ...p, [it.cod_sis]: e.target.value }))} 
                                placeholder="Nova OS"
                              />
                            </td>
                            <td>{it.lote || '-'}</td>
                            <td>
                              <select 
                                className="form-select" 
                                value={(typeof moveLote === 'object' ? moveLote?.nome : moveLote) || it.lote || ''} 
                                onChange={(e) => setMoveLote(e.target.value)}
                              >
                                <option value="">Selecionar Lote</option>
                                {lotesDisponiveis.map(lote => (
                                  <option key={lote} value={lote}>{lote}</option>
                                ))}
                              </select>
                            </td>
                            <td>{it.status || '-'}</td>
                            <td>
                              <select 
                                className="form-select" 
                                value={moveStatus || it.status || ''} 
                                onChange={(e) => setMoveStatus(e.target.value)}
                              >
                                <option value="">Selecionar Status</option>
                                {statuses.map(status => (
                                  <option key={status} value={status}>{status}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input 
                                className="form-input" 
                                value={moveNfDevolucao || it.nf_devolucao || ''} 
                                onChange={(e) => setMoveNfDevolucao(e.target.value)} 
                                placeholder="NF Devolução"
                              />
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button 
                                  className="btn btn-sm btn-danger" 
                                  onClick={() => { setScanned(s => s.filter(c => c !== it.cod_sis)); setOsMap(p => { const n = { ...p }; delete n[it.cod_sis]; return n; }); }}
                                  title="Remover"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="results-card">
                  <div className="card-header">
                    <h4>❌ Não encontrados ({scannedInfo.missing.length})</h4>
                  </div>
                  <div className="missing-list">
                    {scannedInfo.missing.map(code => (
                      <div key={code} className="missing-item">
                        <span>{code}</span>
                        <button 
                          className="btn btn-sm btn-danger" 
                          onClick={() => setScanned(s => s.filter(c => c !== code))}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowMove(false); setScanned([]); setOsMap({}); setPendingCode(''); setPendingOS(''); }}>❌ Cancelar</button>
              <button className="btn btn-primary" disabled={!scannedInfo.found.length} onClick={confirmMove}>✅ Confirmar Movimentação ({scannedInfo.found.length} itens)</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Gerar etiqueta por PART NUMBER */}
      {showGenPN && (
        <div className="modal-overlay" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className="modal" style={{ animation: 'slideInUp 0.4s ease-out' }}>
            <div className="modal-header">
              <h3>🏷️ Gerar etiqueta por PART NUMBER</h3>
              <button className="btn btn-sm btn-outline" onClick={() => setShowGenPN(false)}>✕ Fechar</button>
            </div>
            <div className="modal-body">
              <div className="config-group">
                <label>🔢 PART NUMBER (código da peça)</label>
                <input className="filter-input" placeholder="Ex.: 02850785" value={pnForLabels} onChange={(e) => setPnForLabels(e.target.value)} />
              </div>
              <div className="config-group">
                <label>📊 Status (opcional)</label>
                <select className="filter-select" value={statusForLabels} onChange={(e) => setStatusForLabels(e.target.value)}>
                  <option value="">Qualquer status</option>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            
              {pnForLabels && (
                <div className="results-section">
                  <div className="results-card">
                    <div className="card-header">
                      <h4>📋 {resultsByPN.length} unidade(s) encontrada(s) com o filtro</h4>
                    </div>
                    {resultsByPN.length > 0 && (
                      <div className="items-table">
                        <table>
                          <thead>
                            <tr>
                              <th>COD.VEGA</th>
                              <th>NF</th>
                              <th>DELIVERY</th>
                              <th>Nº PEDIDO</th>
                              <th>VALOR UNIT.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultsByPN.map((it, index) => (
                              <tr key={index}>
                                <td className="item-code">{it.cod_sis || ''}</td>
                                <td>{it.nf || ''}</td>
                                <td>{it.delivery || ''}</td>
                                <td>{it.pedido_cliente || ''}</td>
                                <td className="value-cell">{brl(it.valor_unit)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" disabled={!resultsByPN.length} onClick={() => openLabelsWindow(resultsByPN)}>
                🏷️ Gerar {resultsByPN.length} etiqueta(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pergunta de etiquetas pós-upload */}
      {askLabels && (
        <div className="modal-overlay" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className="modal" style={{ animation: 'slideInUp 0.4s ease-out' }}>
            <div className="modal-header">
              <h3>✅ Itens importados!</h3>
            </div>
            <div className="modal-body">
              <p>Deseja gerar etiquetas para as {pendingCreated.length} peça(s) que acabaram de ser importadas?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={confirmWithoutLabels}>❌ Não, obrigado(a)</button>
              <button className="btn btn-primary" onClick={confirmWithLabels}>🏷️ Sim, gerar etiquetas</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Cadastro Manual de Estoque */}
      {showManualImport && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title-section">
                <div className="modal-icon">📝</div>
                <div>
                  <h3>Cadastro Manual de Estoque</h3>
                  <p className="modal-subtitle">Importe dados via planilha CSV</p>
                </div>
              </div>
              <button className="btn btn-sm btn-outline" onClick={() => setShowManualImport(false)}>
                ✕ Fechar
              </button>
            </div>
            
            <div className="modal-body modal-manual-body">
              <div className="manual-import-content">
                <div className="import-steps">
                  <div className="step-item">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>Baixe o modelo</h4>
                      <p>Faça download da planilha modelo com todas as colunas necessárias</p>
                    </div>
                  </div>
                  
                  <div className="step-arrow">→</div>
                  
                  <div className="step-item">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>Preencha os dados</h4>
                      <p>Complete as informações dos itens na planilha baixada</p>
                    </div>
                  </div>
                  
                  <div className="step-arrow">→</div>
                  
                  <div className="step-item">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>Faça o upload</h4>
                      <p>Carregue o arquivo preenchido para importar os dados</p>
                    </div>
                  </div>
                </div>
                
                <div className="import-actions">
                  {isAdmin() ? (
                    <>
                      <div className="action-card download-card">
                        <div className="action-icon">📥</div>
                        <div className="action-content">
                          <h4>Baixar Modelo</h4>
                          <p>Planilha CSV com todas as colunas necessárias</p>
                          <button 
                            className="btn btn-primary btn-lg action-btn" 
                            onClick={downloadManualTemplateCSV}
                          >
                            <span className="btn-icon">📄</span>
                            Baixar planilha modelo (.csv)
                          </button>
                        </div>
                      </div>
                      
                      <div className="action-card upload-card">
                        <div className="action-icon">📤</div>
                        <div className="action-content">
                          <h4>Carregar Dados</h4>
                          <p>Faça upload da planilha preenchida</p>
                          <label 
                            htmlFor="file-upload-manual" 
                            className="btn btn-secondary btn-lg action-btn upload-btn"
                          >
                            <span className="btn-icon">📁</span>
                            Escolher arquivo CSV
                            <input
                              id="file-upload-manual"
                              type="file"
                              accept=".csv"
                              className="hidden"
                              onChange={(e) => onManualUpload(e.target.files)}
                              ref={fileManualRef}
                            />
                          </label>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
                
                <div className="import-info">
                  <div className="info-card">
                    <div className="info-icon">ℹ️</div>
                    <div className="info-content">
                      <h5>Informações importantes:</h5>
                      <ul>
                        <li>Use apenas arquivos no formato CSV</li>
                        <li>Mantenha o cabeçalho da planilha modelo</li>
                        <li>Preencha pelo menos PART NUMBER e NF</li>
                        <li>Valores monetários devem usar vírgula como separador decimal</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Assinatura */}
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={closeSignatureModal}
        onConfirm={handleSignatureConfirm}
        items={scannedInfo.found.map(item => ({
          ...item,
          nova_localizacao: (typeof moveLocal === 'object' ? moveLocal?.nome : moveLocal) || item.localizacao_fisica,
          nova_os: osMap[item.cod_sis] || item.os_relacionada,
          novo_lote: (typeof moveLote === 'object' ? moveLote?.nome : moveLote) || item.lote,
          novo_status: moveStatus || item.status
        }))}
      />

      {/* Modal de Visualização de Assinaturas */}
      <SignatureViewer
        isOpen={showSignatureViewer}
        onClose={() => setShowSignatureViewer(false)}
        signatureData={selectedItemSignature?.signatureData}
        itemData={selectedItemSignature?.itemData}
      />

      {/* Modal de Retry para Itens Falhados */}
      {showRetryModal && (
        <div className="modal-overlay">
          <div className="modal modal-wide">
            <div className="modal-header">
              <h3>🔧 Corrigir Itens Falhados</h3>
              <p>Corrija os dados dos itens que falharam e tente inserir novamente</p>
            </div>
            <div className="modal-body">
              <div className="retry-items-container">
                <div className="retry-summary">
                  <span className="retry-count">{retryItems.length} item(s) falharam</span>
                  <span className="retry-corrected">
                    {retryItems.filter(item => item.corrected).length} corrigido(s)
                  </span>
                </div>
                
                <div className="retry-items-list">
                  {retryItems.map((item, index) => (
                    <div key={index} className={`retry-item ${item.corrected ? 'corrected' : ''}`}>
                      <div className="retry-item-header">
                        <span className="item-cod-sis">{item.cod_sis}</span>
                        <span className="item-error">{item.error}</span>
                        {item.corrected && <span className="corrected-badge">✓ Corrigido</span>}
                      </div>
                      
                      <div className="retry-item-fields">
                        <div className="field-group">
                          <label>COD.SIS:</label>
                          <input
                            type="text"
                            value={item.cod_sis || ''}
                            onChange={(e) => updateRetryItem(index, 'cod_sis', e.target.value.replace(/[^0-9]/g, '').slice(0, 7))}
                            placeholder="0000000"
                          />
                        </div>
                        <div className="field-group">
                          <label>Part Number:</label>
                          <input
                            type="text"
                            value={item.part_number || ''}
                            onChange={(e) => updateRetryItem(index, 'part_number', e.target.value)}
                            placeholder="Part Number"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>NF:</label>
                          <input
                            type="text"
                            value={item.nf || ''}
                            onChange={(e) => updateRetryItem(index, 'nf', e.target.value)}
                            placeholder="Número da NF"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>Valor Unit.:</label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.valor_unit || ''}
                            onChange={(e) => updateRetryItem(index, 'valor_unit', parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>Status:</label>
                          <select
                            value={item.status || 'DISPONÍVEL EM ESTOQUE'}
                            onChange={(e) => updateRetryItem(index, 'status', e.target.value)}
                          >
                            {statuses.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="field-group">
                          <label>Localização Física:</label>
                          <select
                            value={item.localizacao_fisica || 'Estoque'}
                            onChange={(e) => updateRetryItem(index, 'localizacao_fisica', e.target.value)}
                          >
                            {locais.map(local => (
                              <option key={local} value={local}>{local}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="field-group">
                          <label>Lote:</label>
                          <select
                            value={item.lote || ''}
                            onChange={(e) => updateRetryItem(index, 'lote', e.target.value)}
                          >
                            <option value="">Selecione um lote</option>
                            {lotes.map(lote => (
                              <option key={lote.id || lote} value={lote.nome || lote}>
                                {lote.nome || lote}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-outline" 
                onClick={closeRetryModal}
                disabled={retryLoading}
              >
                ❌ Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={retryFailedItems}
                disabled={retryLoading || retryItems.filter(item => item.corrected).length === 0}
              >
                {retryLoading ? '⏳ Processando...' : '🔄 Tentar Novamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}